import React from 'react';
import PropTypes from 'prop-types';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import {
  IconButton,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
} from '@material-ui/core';
import {
  Add,
  DeleteForever,
  Lock,
  LockOpen,
  PersonAdd,
} from '@material-ui/icons';
import ActionButton from '../../../components/ActionButton';
import CustomTable from '../components/CustomTable';
import EditableCell from '../components/EditableCell';

const useStyles = makeStyles((theme) => ({
  paper: {
    width: '100%',
    padding: theme.spacing(4, 8),
    boxSizing: 'border-box',
  },
  titleWrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(2),
  },
  title: {
    fontSize: 30,
  },
  actionWrapper: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
  },
  addButton: {
    '&:hover': {
      backgroundColor: theme.palette.primary.main,
    },
  },
  centeredCellContent: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%',
  },
}));

const ElencoRepartiTable = ({
  addNewRow,
  elencoReparti,
  getResponsabiliEmailFromId,
  handleCellChange,
  handleSave,
  loading,
  onClickAssociaResponsabili,
  onRemoveRowReparti,
  isEditingDisabledForConsulente,
  onToggleEditingForConsulente,
}) => {
  const classes = useStyles();
  const theme = useTheme();

  const isConsulente =
    localStorage.getItem('superconsulente') === 'false' &&
    localStorage.getItem('consulenteId') !== 'null';

  const isEditingBlocked =
    isConsulente && isEditingDisabledForConsulente.elencoReparti;

  const columns = [
    {
      Header: 'Reparto',
      id: 'reparto',
      accessor: (d) => d.nome,
      Cell: (props) => (
        <div className={classes.centeredCellContent}>
          <EditableCell
            value={props.value}
            disabled={isEditingBlocked}
            onChange={(newValue) =>
              handleCellChange(props.index, 'nome', newValue)
            }
          />
        </div>
      ),
    },
    {
      Header: 'Email Utenza',
      id: 'emailUtente',
      accessor: (d) => d,
      Cell: (props) => (
        <div className={classes.centeredCellContent}>
          <TextField
            fullWidth
            InputProps={{ disableUnderline: true }}
            inputProps={{
              style: { textAlign: 'center', cursor: 'not-allowed' },
            }}
            disabled
            value={
              Array.isArray(props.value?.responsabili) &&
              props.value.responsabili.length > 0
                ? getResponsabiliEmailFromId(props.value.responsabili)
                : ''
            }
          />
        </div>
      ),
    },
    {
      Header: 'Associa Utenza',
      id: 'associaUtente',
      width: 240,
      sortable: false,
      accessor: (d) => d,
      Cell: (props) => (
        <IconButton
          onClick={() => onClickAssociaResponsabili(props.value)}
          disabled={isEditingBlocked}
          style={{
            color: isEditingBlocked
              ? theme.palette.disabled.main
              : theme.palette.primary.main,
          }}
        >
          <PersonAdd />
        </IconButton>
      ),
    },
    {
      Header: 'Elimina',
      id: 'eliminaDefinizioneReparto',
      width: 120,
      sortable: false,
      accessor: (d) => d,
      Cell: (props) => (
        <div className={classes.centeredCellContent}>
          <IconButton
            disabled={props.value.repartoDefault || isEditingBlocked}
            onClick={() => onRemoveRowReparti(props)}
            style={{
              color:
                props.value.repartoDefault || isEditingBlocked
                  ? theme.palette.disabled.main
                  : theme.palette.primary.main,
            }}
          >
            <DeleteForever />
          </IconButton>
        </div>
      ),
    },
  ];

  return (
    <Paper className={classes.paper}>
      <div className={classes.titleWrapper}>
        <Typography variant="h5" className={classes.title}>
          Elenco reparti
        </Typography>

        <div className={classes.actionWrapper}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<Add />}
            onClick={() => addNewRow('elencoReparti')}
            title="Aggiungi reparto"
            disabled={isEditingBlocked}
            className={classes.addButton}
          >
            Reparto
          </Button>

          <IconButton
            disabled={isConsulente}
            onClick={() => onToggleEditingForConsulente('elencoReparti')}
          >
            {isEditingDisabledForConsulente.elencoReparti ? (
              <Lock titleAccess="Modifica bloccata" />
            ) : (
              <LockOpen titleAccess="Modifica consentita" />
            )}
          </IconButton>
        </div>
      </div>
      <CustomTable
        records={elencoReparti}
        columns={columns}
        defaultPageSize={5}
        loading={loading}
        filterable={false}
        resizable={true}
        sortable={true}
      />
      <Box mt={3} display="flex" justifyContent="flex-end">
        <ActionButton
          label="Salva modifiche"
          onClick={handleSave}
          disabled={isEditingBlocked}
        />
      </Box>
    </Paper>
  );
};

ElencoRepartiTable.propTypes = {
  addNewRow: PropTypes.func.isRequired,
  elencoReparti: PropTypes.array.isRequired,
  getResponsabiliEmailFromId: PropTypes.func.isRequired,
  handleCellChange: PropTypes.func.isRequired,
  handleSave: PropTypes.func.isRequired,
  isEditingDisabledForConsulente: PropTypes.object.isRequired,
  loading: PropTypes.bool.isRequired,
  onClickAssociaResponsabili: PropTypes.func.isRequired,
  onRemoveRowReparti: PropTypes.func.isRequired,
  onToggleEditingForConsulente: PropTypes.func.isRequired,
};

export default ElencoRepartiTable;
